import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";

import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { useAuth } from "../../../helpers/firebase";
import { useSubscriptionType } from "../../../helpers/hooks/companyData";
import { getAccountId } from "../../../helpers/accountHelpers";

const CONFIG_URL =
  "https://us-central1-aclymate-internal.cloudfunctions.net/npsGetConfig";
const SUBMIT_URL =
  "https://us-central1-aclymate-internal.cloudfunctions.net/npsSubmit";
const SUBMIT_TIMEOUT_MS = 10000;
const IS_PRODUCTION = process.env.REACT_APP_ENVIRONMENT === "production";

const DAY_MS = 24 * 60 * 60 * 1000;
const LAST_SHOWN_KEY = "npsLastShownAt";
const SNOOZED_UNTIL_KEY = "npsSnoozedUntil";

// Non-production-only bypass — append ?npsPreview=1 to any platform URL to
// render the widget immediately, ignoring path/frequency/snooze/sampling.
// The config fetch still runs (a real published draft renders as-is); this
// stub only covers the case where nothing has been published yet. Submitting
// under preview never reaches the real endpoint — see handleSubmit.
const PREVIEW_CONFIG = {
  enabled: true,
  style: { primaryColor: "#0d4d4d", position: "bottom" },
  content: {
    question:
      "On a scale of 0-10, how likely are you to recommend Aclymate to another business in your network?",
    feedbackQuestion: "We use your feedback for improvements - why did you choose that score?"
  },
  audience: { pathRules: [], frequencyDays: 30, snoozeDays: 3, sampling: 100 }
};

const safeStorageGet = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeStorageSet = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage may be unavailable (private browsing, disabled cookies) —
    // suppression/sampling just won't persist across reloads in that case.
  }
};

// Deterministic 32-bit-ish string hash — used both for the stable per-user
// sampling bucket and as a fingerprint of the published config, so frequency
// and snooze state don't carry over when staff publish a new campaign.
const hashString = (value) =>
  value
    .split("")
    .reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0, 0)
    .toString(36);

const hashToBucket = (value) => parseInt(hashString(value), 36) % 100;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const sanitizeConfig = (rawConfig) => {
  if (!rawConfig || typeof rawConfig !== "object" || rawConfig.enabled !== true) {
    return null;
  }

  const content = rawConfig.content || {};
  const hasRequiredContent =
    typeof content.question === "string" && typeof content.feedbackQuestion === "string";
  if (!hasRequiredContent) {
    return null;
  }

  const style = rawConfig.style || {};
  const audience = rawConfig.audience || {};

  return {
    enabled: true,
    style: {
      primaryColor: typeof style.primaryColor === "string" ? style.primaryColor : "#0d4d4d",
      position: style.position === "top" ? "top" : "bottom"
    },
    content,
    audience: {
      pathRules: Array.isArray(audience.pathRules) ? audience.pathRules : [],
      frequencyDays: Number.isFinite(audience.frequencyDays)
        ? Math.max(audience.frequencyDays, 0)
        : 30,
      snoozeDays: Number.isFinite(audience.snoozeDays) ? Math.max(audience.snoozeDays, 0) : 3,
      sampling: Number.isFinite(audience.sampling) ? clamp(audience.sampling, 0, 100) : 100
    }
  };
};

const storageKey = (base, { configVersion, uid, companyId }) =>
  `${base}:${configVersion}:${uid}:${companyId}`;

const isEligible = ({ config, pathname, uid, companyId }) => {
  const pathMatches = (config.audience.pathRules || []).some((rule) =>
    pathname.endsWith(rule)
  );
  if (!pathMatches) {
    return false;
  }

  const keyParts = { configVersion: hashString(JSON.stringify(config)), uid, companyId };

  const snoozedUntil = Number(safeStorageGet(storageKey(SNOOZED_UNTIL_KEY, keyParts)) || 0);
  if (Date.now() < snoozedUntil) {
    return false;
  }

  const lastShownAt = Number(safeStorageGet(storageKey(LAST_SHOWN_KEY, keyParts)) || 0);
  const frequencyMs = config.audience.frequencyDays * DAY_MS;
  if (Date.now() - lastShownAt < frequencyMs) {
    return false;
  }

  return hashToBucket(`${uid}:${companyId}`) < config.audience.sampling;
};

const ScoreButtons = ({ onSelect }) => (
  <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="center">
    {Array(11)
      .fill()
      .map((_, value) => (
        <Button
          key={value}
          variant="outlined"
          size="small"
          onClick={() => onSelect(value)}
          sx={{ minWidth: 32, color: "inherit", borderColor: "inherit" }}
        >
          {value}
        </Button>
      ))}
  </Stack>
);

const FeedbackForm = ({
  feedbackQuestion,
  feedback,
  onChangeFeedback,
  onBack,
  onSubmit,
  submitting,
  submitFailed
}) => (
  <Stack spacing={1} sx={{ width: "100%", maxWidth: 480 }}>
    <Typography variant="caption" color="inherit" id="nps-feedback-label">
      {feedbackQuestion}
    </Typography>
    <TextField
      value={feedback}
      onChange={(event) => onChangeFeedback(event.target.value)}
      inputProps={{ "aria-labelledby": "nps-feedback-label" }}
      multiline
      minRows={2}
      fullWidth
      size="small"
      sx={{ bgcolor: "background.paper", borderRadius: 1 }}
    />
    {submitFailed ? (
      <Typography variant="caption" color="inherit">
        That didn&apos;t go through — please try again.
      </Typography>
    ) : null}
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      <Button size="small" onClick={onBack} sx={{ color: "inherit" }}>
        Back
      </Button>
      <Button
        size="small"
        variant="contained"
        onClick={onSubmit}
        disabled={submitting}
      >
        {submitting ? "Submitting…" : "Submit"}
      </Button>
    </Stack>
  </Stack>
);

const NpsWidgetInner = () => {
  const theme = useTheme();
  const { pathname, search } = useLocation();
  const [user] = useAuth();
  const [{ status: subscriptionStatus }] = useSubscriptionType("saas");
  const companyId = getAccountId();

  const [config, setConfig] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [score, setScore] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isPreview = !IS_PRODUCTION && new URLSearchParams(search).has("npsPreview");

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await fetch(CONFIG_URL);
        if (!response.ok) {
          setConfig(null);
          return;
        }
        const data = await response.json();
        setConfig(sanitizeConfig(data));
      } catch {
        setConfig(null);
      }
    };

    loadConfig();
  }, []);

  const effectiveConfig = config || (isPreview ? PREVIEW_CONFIG : null);
  const eligible = isPreview
    ? !!user && !!effectiveConfig
    : !!user &&
      !!effectiveConfig &&
      isEligible({ config: effectiveConfig, pathname, uid: user.uid, companyId });

  if (dismissed || submitted || !eligible) {
    return null;
  }

  const keyParts = {
    configVersion: hashString(JSON.stringify(effectiveConfig)),
    uid: user.uid,
    companyId
  };

  const handleSnooze = () => {
    const snoozeDays = effectiveConfig.audience.snoozeDays;
    safeStorageSet(
      storageKey(SNOOZED_UNTIL_KEY, keyParts),
      String(Date.now() + snoozeDays * DAY_MS)
    );
    setDismissed(true);
  };

  const handleSubmit = async () => {
    // Both endpoints are hardcoded to the one live aclymate-internal backend
    // — there's no per-renew-west-environment NPS deployment to route to.
    // Outside production (isPreview already implies this), a submission
    // always resolves locally instead of polluting real NPS rows from a
    // normal QA visit on test/demo/staging.
    if (!IS_PRODUCTION) {
      setSubmitted(true);
      return;
    }

    setSubmitting(true);
    setSubmitFailed(false);

    // One deadline covers getIdToken() and the fetch together — fetch
    // respects the abort signal natively, but getIdToken() has no
    // cancellation of its own, so a hung token refresh needs raceAgainstAbort
    // or it would leave the Submit button disabled forever past the abort.
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), SUBMIT_TIMEOUT_MS);
    const raceAgainstAbort = (promise) =>
      new Promise((resolve, reject) => {
        if (timeoutController.signal.aborted) {
          reject(new Error("aborted"));
          return;
        }
        timeoutController.signal.addEventListener("abort", () => reject(new Error("aborted")), {
          once: true
        });
        promise.then(resolve, reject);
      });

    try {
      // The backend derives email/uid itself from this token — it no longer
      // trusts client-supplied identity fields, so there's nothing left here
      // for a caller to spoof.
      const idToken = await raceAgainstAbort(user.getIdToken());
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        signal: timeoutController.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          score,
          feedback: feedback || null,
          companyId,
          plan: subscriptionStatus,
          page: pathname
        })
      });

      if (!response.ok) {
        setSubmitFailed(true);
        return;
      }

      safeStorageSet(storageKey(LAST_SHOWN_KEY, keyParts), String(Date.now()));
      setSubmitted(true);
    } catch {
      setSubmitFailed(true);
    } finally {
      clearTimeout(timeoutId);
      setSubmitting(false);
    }
  };

  const edgeSx =
    effectiveConfig.style.position === "top" ? { top: 0 } : { bottom: 0 };

  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        ...edgeSx,
        zIndex: theme.zIndex.modal + 1,
        bgcolor: effectiveConfig.style.primaryColor,
        color: theme.palette.common.white,
        p: 2
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="flex-start"
        justifyContent="center"
        sx={{ position: "relative" }}
      >
        <Stack spacing={1.5} alignItems="center" sx={{ maxWidth: 640 }}>
          <Typography variant="body2" color="inherit" align="center">
            {effectiveConfig.content.question}
          </Typography>
          {score === null ? (
            <ScoreButtons onSelect={setScore} />
          ) : (
            <FeedbackForm
              feedbackQuestion={effectiveConfig.content.feedbackQuestion}
              feedback={feedback}
              onChangeFeedback={setFeedback}
              onBack={() => setScore(null)}
              onSubmit={handleSubmit}
              submitting={submitting}
              submitFailed={submitFailed}
            />
          )}
          <Button size="small" onClick={handleSnooze} sx={{ color: "inherit" }}>
            Ask me later
          </Button>
        </Stack>
        <IconButton
          size="small"
          onClick={() => setDismissed(true)}
          aria-label="Close survey"
          sx={{ position: "absolute", right: 0, top: 0, color: "inherit" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
};

// A survey widget is decorative, not load-bearing — if it throws (a malformed
// config, storage unavailable, anything unforeseen) it should disappear
// quietly rather than take the platform page down with it.
const NpsWidget = () => (
  <Sentry.ErrorBoundary fallback={null}>
    <NpsWidgetInner />
  </Sentry.ErrorBoundary>
);

export default NpsWidget;
